import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ClipboardList, Save, Plus, Trash2, RotateCcw, ArrowUp, ArrowDown,
  Eye, EyeOff, ArrowLeft, Car, Bike, Box,
} from "lucide-react";
import { api, formatApiError } from "../../lib/api";

const CATEGORY_META = {
  car:       { label: "Car",       icon: Car,  description: "Cars, SUVs, vans, pickup trucks." },
  motorbike: { label: "Motorbike", icon: Bike, description: "Motorcycles, scooters, mopeds." },
  general:   { label: "General",   icon: Box,  description: "Fallback for anything else." },
};

const SCOPE_META = {
  pickup: { label: "Pickup", description: "Checked when handing the vehicle to the customer." },
  return: { label: "Return", description: "Checked when the customer returns the vehicle." },
};

export default function InspectionChecklistSettings() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState("pickup");
  const [category, setCategory] = useState("car");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/checklist-templates");
      setTemplates(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const current = useMemo(() => {
    return templates.find((t) => t.scope === scope && t.category === category) || null;
  }, [templates, scope, category]);

  const updateLocal = (next) => {
    setTemplates((cur) => cur.map((t) => (t.id === next.id ? next : t)));
  };

  const saveTemplate = async () => {
    if (!current) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/checklist-templates/${current.id}`, {
        name: current.name,
        items: current.items.map((it, idx) => ({
          key: it.key,
          label: it.label,
          order: idx + 1,
          active: it.active !== false,
          custom: !!it.custom,
        })),
      });
      updateLocal(data);
      toast.success("Template saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const resetTemplate = async () => {
    if (!current) return;
    if (!window.confirm("Reset this template to default items? Custom items will be removed. Existing finalised inspections are NOT affected.")) return;
    try {
      const { data } = await api.post(`/checklist-templates/${current.id}/reset`);
      updateLocal(data);
      toast.success("Template reset to defaults");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onItemChange = (idx, field, value) => {
    if (!current) return;
    const items = [...current.items];
    items[idx] = { ...items[idx], [field]: value };
    updateLocal({ ...current, items });
  };

  const moveItem = (idx, dir) => {
    if (!current) return;
    const items = [...current.items];
    const ni = dir === "up" ? idx - 1 : idx + 1;
    if (ni < 0 || ni >= items.length) return;
    const [m] = items.splice(idx, 1);
    items.splice(ni, 0, m);
    updateLocal({ ...current, items });
  };

  const removeItem = (idx) => {
    if (!current) return;
    const it = current.items[idx];
    if (!it.custom && !window.confirm(`'${it.label}' is a default item. Remove it from this template?`)) return;
    const items = current.items.filter((_, i) => i !== idx);
    updateLocal({ ...current, items });
  };

  const addItem = () => {
    if (!current) return;
    const newKey = `custom_${Date.now().toString(36)}`;
    const items = [...current.items, {
      key: newKey, label: "New checklist item", order: current.items.length + 1,
      active: true, custom: true,
    }];
    updateLocal({ ...current, items });
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="checklist-settings-page">
      <Link to="/app/settings" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4" /> Back to Settings
      </Link>

      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Inspections</div>
        <h1 className="font-display text-3xl font-bold mt-1">Checklist templates</h1>
        <p className="text-sm text-slate-500 mt-1 max-w-2xl">
          Customise what your staff checks at pickup and return for each vehicle category.
          Templates apply to <b>future</b> inspections only: every past inspection keeps its own snapshot, locked.
        </p>
      </div>

      {/* Scope tabs */}
      <div className="flex gap-1 p-1 rounded-xl bg-slate-100 w-fit" data-testid="checklist-scope-tabs">
        {Object.entries(SCOPE_META).map(([k, m]) => (
          <button
            key={k}
            onClick={() => setScope(k)}
            data-testid={`checklist-scope-${k}`}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition ${
              scope === k ? "bg-white shadow-sm text-[#1B4332]" : "text-slate-600 hover:text-slate-900"
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>

      {/* Category cards */}
      <div className="grid sm:grid-cols-3 gap-3" data-testid="checklist-category-cards">
        {Object.entries(CATEGORY_META).map(([k, m]) => {
          const Icon = m.icon;
          const tpl = templates.find((t) => t.scope === scope && t.category === k);
          return (
            <button
              key={k}
              onClick={() => setCategory(k)}
              data-testid={`checklist-category-${k}`}
              className={`text-left p-4 rounded-xl border transition ${
                category === k
                  ? "border-[#1B4332] bg-[#1B4332]/5 ring-1 ring-[#1B4332]"
                  : "border-slate-200 bg-white hover:bg-slate-50"
              }`}
            >
              <div className="flex items-center gap-2">
                <Icon className="w-4 h-4 text-[#1B4332]" />
                <span className="font-medium">{m.label}</span>
                {tpl && (
                  <span className="ml-auto text-xs text-slate-500">
                    {tpl.items.filter((it) => it.active !== false).length} active
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-1">{m.description}</p>
            </button>
          );
        })}
      </div>

      {/* Editor */}
      {loading ? (
        <div className="text-sm text-slate-500" data-testid="checklist-loading">Loading…</div>
      ) : !current ? (
        <div className="text-sm text-slate-500">No template found.</div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white" data-testid="checklist-editor">
          <div className="flex items-center justify-between flex-wrap gap-3 px-5 py-3 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <ClipboardList className="w-4 h-4 text-[#1B4332]" />
              <input
                type="text"
                value={current.name || ""}
                onChange={(e) => updateLocal({ ...current, name: e.target.value })}
                data-testid="checklist-template-name"
                className="text-sm font-semibold border-b border-transparent hover:border-slate-300 focus:border-[#1B4332] focus:outline-none px-1 py-0.5 min-w-[260px]"
              />
              {current.is_default && (
                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">
                  Default
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={resetTemplate}
                data-testid="checklist-reset-btn"
                className="px-3 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-xs inline-flex items-center gap-1"
              >
                <RotateCcw className="w-3 h-3" /> Reset
              </button>
              <button
                onClick={saveTemplate}
                disabled={saving}
                data-testid="checklist-save-btn"
                className="px-3 py-1.5 rounded-md btn-brand text-xs inline-flex items-center gap-1 disabled:opacity-60"
              >
                <Save className="w-3 h-3" /> {saving ? "Saving…" : "Save changes"}
              </button>
            </div>
          </div>

          <ul className="divide-y divide-slate-100" data-testid="checklist-items">
            {current.items.map((it, idx) => {
              const inactive = it.active === false;
              return (
                <li
                  key={it.key + "-" + idx}
                  className={`px-5 py-3 flex flex-wrap items-center gap-3 ${inactive ? "opacity-60 bg-slate-50" : ""}`}
                  data-testid={`checklist-item-${it.key}`}
                >
                  <span className="text-xs font-mono text-slate-400 w-6 text-right">{idx + 1}.</span>
                  <input
                    type="text"
                    value={it.label}
                    onChange={(e) => onItemChange(idx, "label", e.target.value)}
                    data-testid={`checklist-item-label-${it.key}`}
                    className="flex-1 min-w-[200px] text-sm px-2 py-1.5 rounded-md border border-transparent hover:border-slate-300 focus:border-[#1B4332] focus:outline-none"
                  />
                  {it.custom ? (
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                      Custom
                    </span>
                  ) : null}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => moveItem(idx, "up")}
                      disabled={idx === 0}
                      data-testid={`checklist-item-up-${it.key}`}
                      className="p-1.5 rounded-md text-slate-600 hover:bg-slate-100 disabled:opacity-30"
                      title="Move up"
                    >
                      <ArrowUp className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => moveItem(idx, "down")}
                      disabled={idx === current.items.length - 1}
                      data-testid={`checklist-item-down-${it.key}`}
                      className="p-1.5 rounded-md text-slate-600 hover:bg-slate-100 disabled:opacity-30"
                      title="Move down"
                    >
                      <ArrowDown className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => onItemChange(idx, "active", inactive)}
                      data-testid={`checklist-item-toggle-${it.key}`}
                      className="p-1.5 rounded-md text-slate-600 hover:bg-slate-100"
                      title={inactive ? "Activate" : "Deactivate"}
                    >
                      {inactive ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    </button>
                    <button
                      onClick={() => removeItem(idx)}
                      data-testid={`checklist-item-remove-${it.key}`}
                      className="p-1.5 rounded-md text-rose-700 hover:bg-rose-50"
                      title="Remove from this template"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>

          <div className="px-5 py-3 border-t border-slate-100">
            <button
              onClick={addItem}
              data-testid="checklist-add-item-btn"
              className="px-3 py-1.5 rounded-md border border-dashed border-slate-300 bg-white hover:bg-slate-50 text-sm inline-flex items-center gap-1.5 text-slate-700"
            >
              <Plus className="w-3.5 h-3.5" /> Add custom item
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
